import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@supabase/supabase-js'
import { auth, profiles } from '@/services/supabase'
import type { Profile, AuthResult } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const profile = ref<Profile | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => profile.value?.role === 'admin')
  const fullName = computed(() => 
    profile.value?.full_name || user.value?.email?.split('@')[0] || 'Usuário'
  )

  // Lock para evitar chamadas concorrentes a initialize()
  let initPromise: Promise<void> | null = null

  // Actions
  async function initialize(): Promise<void> {
    if (initialized.value) return
    if (initPromise) return initPromise
    initPromise = doInitialize()
    return initPromise
  }

  async function doInitialize(): Promise<void> {
    loading.value = true
    try {
      const { session } = await auth.getSession()

      if (session?.user) {
        user.value = session.user
        await fetchProfile()
      }

      // Listener para mudanças de auth
      auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event)

        if (event === 'SIGNED_IN' && session?.user) {
          user.value = session.user
          await fetchProfile()
        } else if (event === 'SIGNED_OUT') {
          user.value = null
          profile.value = null
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          user.value = session.user
        }
      })
    } catch (error) {
      console.error('Error initializing auth:', error)
    } finally {
      loading.value = false
      initialized.value = true
      initPromise = null
    }
  }

  async function fetchProfile(): Promise<void> {
    if (!user.value?.id) return

    const { data, error } = await profiles.get(user.value.id)
    console.log('Profile fetched:', { data, error, role: data?.role })
    if (!error && data) {
      profile.value = data
    } else if (error) {
      console.error('Error fetching profile:', error)
    }
  }

  async function signUp(
    email: string,
    password: string,
    metadata: Record<string, unknown> = {}
  ): Promise<AuthResult> {
    loading.value = true
    try {
      const { data, error } = await auth.signUp(email, password, metadata)

      if (error) throw error

      // Supabase pode retornar user mesmo sem confirmação de email
      if (data?.user && !data.user.email_confirmed_at) {
        return {
          success: true,
          needsConfirmation: true,
          message: 'Verifique seu e-mail para confirmar a conta.'
        }
      }

      return { success: true }
    } catch (error) {
      console.error('Sign up error:', error)
      return {
        success: false,
        error: translateAuthError((error as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  async function signIn(email: string, password: string): Promise<AuthResult> {
    loading.value = true
    try {
      const { data, error } = await auth.signIn(email, password)

      if (error) throw error

      user.value = data?.user ?? null
      await fetchProfile()

      return { success: true }
    } catch (error) {
      console.error('Sign in error:', error)
      return {
        success: false,
        error: translateAuthError((error as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  async function signOut(): Promise<AuthResult> {
    loading.value = true
    try {
      const { error } = await auth.signOut()
      if (error) throw error
      user.value = null
      profile.value = null
      initialized.value = false
      return { success: true }
    } catch (error) {
      console.error('Sign out error:', error)
      user.value = null
      profile.value = null
      return { success: false, error: (error as Error).message }
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(email: string): Promise<AuthResult> {
    loading.value = true
    try {
      const { error } = await auth.resetPassword(email)
      if (error) throw error
      return {
        success: true,
        message: 'E-mail de recuperação enviado. Verifique sua caixa de entrada.'
      }
    } catch (error) {
      console.error('Reset password error:', error)
      return {
        success: false,
        error: translateAuthError((error as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  async function updatePassword(newPassword: string): Promise<AuthResult> {
    loading.value = true
    try {
      const { error } = await auth.updatePassword(newPassword)
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Update password error:', error)
      return {
        success: false,
        error: translateAuthError((error as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  async function updateProfile(updates: Partial<Profile>): Promise<AuthResult> {
    if (!user.value?.id) return { success: false, error: 'Não autenticado' }

    loading.value = true
    try {
      const { data, error } = await profiles.update(user.value.id, updates)
      if (error) throw new Error(error)

      profile.value = data
      return { success: true }
    } catch (error) {
      console.error('Update profile error:', error)
      return { success: false, error: (error as Error).message }
    } finally {
      loading.value = false
    }
  }

  // Helper para traduzir erros de autenticação
  function translateAuthError(message: string): string {
    const translations: Record<string, string> = {
      'Invalid login credentials': 'E-mail ou senha incorretos.',
      'Email not confirmed': 'E-mail não confirmado. Verifique sua caixa de entrada.',
      'User already registered': 'Este e-mail já está cadastrado.',
      'Password should be at least 6 characters': 'A senha deve ter pelo menos 6 caracteres.',
      'Unable to validate email address: invalid format': 'Formato de e-mail inválido.',
      'Email rate limit exceeded': 'Muitas tentativas. Aguarde alguns minutos.',
      'New password should be different from the old password': 'A nova senha deve ser diferente da atual.',
    }

    return translations[message] || message || 'Ocorreu um erro. Tente novamente.'
  }

  return {
    // State
    user,
    profile,
    loading,
    initialized,
    // Getters
    isAuthenticated,
    isAdmin,
    fullName,
    // Actions
    initialize,
    fetchProfile,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
  }
})
